import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Flower2, Loader2, Check } from "lucide-react";
import { ROSE_IMAGES, ROSE_COLOR_HEX, roseBackdrop } from "@/lib/roses";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const OPTIONS = [
  { color: "pink", label: "Pink", img: ROSE_IMAGES.pink },
  { color: "red", label: "Red", img: ROSE_IMAGES.red },
  { color: "white", label: "White", img: ROSE_IMAGES.white },
];

export function RoseGiftDialog({ open, onOpenChange, note }) {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const existing = note?.roses?.length || 0;
  const remaining = 3 - existing;

  const toggle = (color) => {
    setSelected((prev) => {
      const count = prev.filter((c) => c === color).length;
      // clicking cycles: add up to remaining, allow multiples of same color
      if (prev.length >= remaining) {
        // remove one of this color if present, else reset
        const idx = prev.lastIndexOf(color);
        if (idx >= 0) return prev.filter((_, i) => i !== idx);
        return prev;
      }
      return [...prev, color];
    });
  };

  const removeAt = (i) => setSelected((prev) => prev.filter((_, idx) => idx !== i));

  const checkout = async () => {
    if (selected.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        colors: selected,
        note_id: note.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const previewRoses = [...(note?.roses || []), ...selected].slice(0, 3);
  const preview = roseBackdrop(previewRoses);
  const total = (selected.length * 0.99).toFixed(2);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#150A1A] border border-pink-500/25 text-pink-50 sm:max-w-lg" data-testid="rose-gift-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl flex items-center gap-2">
            <Flower2 className="w-6 h-6 text-pink-400" /> Gift roses
          </DialogTitle>
          <DialogDescription className="text-slate-400">Attach up to three roses; they bloom behind the note.</DialogDescription>
        </DialogHeader>

        <p className="text-sm text-slate-400 -mt-1">
          Attach up to 3 roses to bloom behind this note. Each rose is <span className="text-pink-300 font-medium">£0.99</span>.
          {existing > 0 && ` This note already has ${existing} rose${existing > 1 ? "s" : ""}.`}
        </p>

        {preview && (
          <div className="relative h-40 rounded-2xl overflow-hidden border border-pink-500/25">
            <img src={preview} alt="preview" className="absolute inset-0 w-full h-full object-cover brightness-[0.55]" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
            <p className="absolute inset-0 flex items-center justify-center font-serif text-xl italic text-pink-50 px-6 text-center">
              {note?.message?.slice(0, 60)}{note?.message?.length > 60 ? "…" : ""}
            </p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          {OPTIONS.map((o) => {
            const count = selected.filter((c) => c === o.color).length;
            const disabled = remaining <= 0;
            return (
              <button
                key={o.color}
                data-testid={`rose-select-${o.color}`}
                onClick={() => toggle(o.color)}
                disabled={disabled}
                className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                  count > 0 ? "border-pink-400 scale-[1.02]" : "border-pink-500/20 hover:border-pink-400/60"
                } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <img src={o.img} alt={o.label} className="h-24 w-full object-cover" />
                <span className="absolute bottom-0 inset-x-0 bg-black/70 text-xs py-1 text-center" style={{ color: ROSE_COLOR_HEX[o.color] }}>
                  {o.label}
                </span>
                {count > 0 && (
                  <span className="absolute top-1 right-1 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="rose-selection-chips">
            {selected.map((c, i) => (
              <button
                key={i}
                onClick={() => removeAt(i)}
                className="text-xs px-3 py-1 rounded-full bg-white/10 border border-pink-500/25 capitalize hover:bg-rose-600/40"
              >
                {c} rose ✕
              </button>
            ))}
          </div>
        )}

        {remaining <= 0 && <p className="text-xs text-rose-400">This note already has the maximum of 3 roses.</p>}

        <button
          data-testid="rose-checkout-btn"
          onClick={checkout}
          disabled={selected.length === 0 || loading}
          className="w-full bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 text-white font-medium py-3 rounded-full shadow-lg shadow-pink-500/25 transition-all hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {selected.length === 0 ? "Select roses" : `Pay £${total} & attach`}
        </button>
      </DialogContent>
    </Dialog>
  );
}
